import { Link } from "react-router-dom";
import SEO, { getServiceSchema, getBreadcrumbSchema } from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2,
  TrendingUp,
  Shield,
  Clock,
  Users,
  DollarSign,
  Target,
  BarChart3,
} from "lucide-react";
import { motion } from "framer-motion";

const WhyHireUs = () => {
  const benefits = [
    {
      icon: DollarSign,
      title: "Maximize Your ROI",
      description:
        "We optimize rent prices based on real-time Kansas City metro market data, achieving 15% higher rents on average than self-managed properties in Overland Park and Johnson County. Our flexible pricing structure (starting at 8% for standard management, with volume discounts available) means you keep more of your profits.",
      stats: "15% Average Rent Increase",
    },
    {
      icon: Clock,
      title: "Save Valuable Time",
      description:
        "From tenant screening to maintenance coordination and rent collection, we handle everything so you can focus on growing your portfolio across Leawood, Lenexa, and Olathe.",
      stats: "Full-Service Management",
    },
    {
      icon: Shield,
      title: "Industry-Leading Collection Rate",
      description:
        "Our proven systems ensure consistent cash flow. We handle late payments, follow Kansas legal requirements, and minimize vacancy with aggressive marketing.",
      stats: "Consistent Cash Flow",
    },
    {
      icon: Users,
      title: "Expert Tenant Screening",
      description:
        "Comprehensive background checks, employment verification, and rental history analysis. We find quality, long-term tenants for your Kansas properties.",
      stats: "< 2% Eviction Rate",
    },
    {
      icon: BarChart3,
      title: "Real-Time Reporting",
      description:
        "Access your owner dashboard 24/7. Track rent payments, maintenance requests, expenses, and property performance from anywhere.",
      stats: "24/7 Dashboard Access",
    },
    {
      icon: Target,
      title: "Local Market Expertise",
      description:
        "Deep knowledge of Johnson County neighborhoods, school districts, and rental demand trends. We know what tenants want in Overland Park vs. Prairie Village.",
      stats: "Established Local Presence",
    },
  ];

  const comparisons = [
    {
      aspect: "Tenant Screening",
      selfManage: "Basic online check, risk of bad tenants",
      professional: "Comprehensive screening, < 2% eviction rate",
    },
    {
      aspect: "Marketing & Leasing",
      selfManage: "Limited reach, longer vacancy periods",
      professional: "Multi-platform syndication, fast vacancy turnaround",
    },
    {
      aspect: "Rent Collection",
      selfManage: "Chasing payments, awkward conversations",
      professional: "Automated system, industry-leading collection rate",
    },
    {
      aspect: "Maintenance Coordination",
      selfManage: "Late-night calls, finding reliable contractors",
      professional: "24/7 support, vetted vendor network",
    },
    {
      aspect: "Legal Compliance",
      selfManage: "Risk of Kansas law violations, costly mistakes",
      professional: "Full compliance, legal protection included",
    },
    {
      aspect: "Time Investment",
      selfManage: "10-15 hours/month per property",
      professional: "Zero time - we handle everything",
    },
  ];

  const schema = [
    getServiceSchema(
      "Professional Property Management",
      "Full-service property management for rental homes in Johnson County, Kansas. Tenant screening, rent collection, maintenance coordination, and financial reporting."
    ),
    getBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Why Hire Us", path: "/why-hire-us" }
    ])
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Why Hire Rently Properties | Kansas City Property Management"
        description="Discover why property owners in Overland Park, Leawood, Lenexa & Olathe choose Rently Properties. Professional rent collection, expert tenant screening, 24/7 maintenance in Johnson County KS."
        canonical="/why-hire-us"
        keywords="why hire property manager, Overland Park property management, Johnson County rental management, Kansas City property manager benefits, JOCO rental income"
        schema={schema}
      />
      <Header />

      <main>
        {/* Hero Section */}
        <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-background via-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Badge className="mb-6" variant="secondary">
              Johnson County's Trusted Property Managers
            </Badge>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6">
              Why Overland Park Owners Choose{" "}
              <span className="text-primary">Rently Properties</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Professional property management that maximizes your ROI while
              eliminating the stress of self-managing rentals in Kansas City
              metro.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-secondary hover:bg-secondary/90" asChild>
                <Link to="/#contact">Get Free Property Analysis</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/management-process">See Our Process</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Benefits Grid */}
        <section className="py-16 sm:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
                The Rently Properties Advantage
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Data-driven property management delivering measurable results
                across Johnson County, Kansas
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                  >
                    <Card className="h-full hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="h-12 w-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
                          <Icon className="h-6 w-6" />
                        </div>
                        <CardTitle className="font-display text-xl mb-2">
                          {benefit.title}
                        </CardTitle>
                        <Badge variant="outline" className="w-fit">
                          {benefit.stats}
                        </Badge>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {benefit.description}
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Self-Manage vs Professional Comparison */}
        <section className="py-16 sm:py-20 bg-muted/30">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
                Self-Manage vs. Professional Management
              </h2>
              <p className="text-lg text-muted-foreground">
                The real cost of managing your own Kansas rental property
              </p>
            </div>

            <div className="space-y-4">
              {comparisons.map((comparison, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  <Card>
                    <CardContent className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h3 className="font-display font-semibold text-foreground mb-2">
                            {comparison.aspect}
                          </h3>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">
                            Self-Manage
                          </div>
                          <p className="text-sm text-destructive">
                            {comparison.selfManage}
                          </p>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">
                            With Rently
                          </div>
                          <p className="text-sm text-primary font-medium flex items-start gap-2">
                            <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            {comparison.professional}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 sm:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Ready to Maximize Your Kansas Rental Investment?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join satisfied property owners throughout Overland Park, Leawood,
              Lenexa, and Olathe who trust Rently Properties.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-secondary hover:bg-secondary/90" asChild>
                <Link to="/#contact">Schedule Free Consultation</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/marketing-process">Learn About Our Marketing</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default WhyHireUs;
